<template>
  <div class="card-detail-view">
    <header class="top-header">
      <div class="header-inner">
        <img src="@/assets/images/logoBienestarAztk.png" alt="Bienestar Azteca Digital" class="header-logo" width="160"
          height="80" />
      </div>
    </header>
    <div class="title-bar">
      <button class="back-btn" @click="$emit('close')" aria-label="Volver">
        <svg class="arrow-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
      </button>
      <h1 class="page-title">Información para usuarios</h1>
    </div>
    <section class="detail-hero" :class="{ 'content-enter': isAnimating }">
      <div class="container">
        <div class="content">
          <div class="image-wrapper" :class="{
            'align-cuenta': currentCard.shortTitle.includes('mi cuenta'),
            'align-mayor': currentCard.shortTitle.includes('mayor de edad')
          }">
            <img :src="currentCard.backImg" :alt="currentCard.title" class="detail-img" />
          </div>
          <div class="text-content">
            <h2 class="detail-title" v-html="currentCard.title"></h2>
            <ul v-if="currentCard.bullets" class="detail-bullets desktop-description">
              <li v-for="(bullet, idx) in currentCard.bullets" :key="idx" v-html="bullet"></li>
            </ul>
            <p v-else class="detail-description desktop-description" v-html="currentCard.description"></p>
            <div class="mobile-description">
              <ul v-if="currentCard.bullets" class="detail-bullets-mobile">
                <li v-for="(bullet, idx) in currentCard.bullets" :key="idx" v-html="bullet"></li>
              </ul>
              <template v-else>
                <p v-for="(paragraph, idx) in currentCard.descriptionMobile" :key="idx" v-html="paragraph"></p>
              </template>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="info-carousel-section">
      <div class="wrap">
        <h3 class="carousel-title">Información para usuarios:</h3>
        <div class="cards" ref="carousel" @scroll="handleScroll">
          <article class="card" v-for="(item, index) in allCards" :key="index"
            :class="{ 'card-selected': isSelectedCard(item) }" @click="handleCardChange(item, index)">
            <div class="card-img"><img :src="item.img" :alt="item.title" width="200" height="200" /></div>
            <div class="card-body">
              <h4>{{ item.shortTitle }}</h4>
            </div>
          </article>
        </div>
        <div class="dots" aria-label="Indicador de carrusel">
          <span class="dot" :class="{ active: currentBlock === 0 }" @click="scrollToBlock(0)"></span>
          <span class="dot" :class="{ active: currentBlock === 1 }" @click="scrollToBlock(1)"></span>
        </div>
        <footer class="legal-footer">
          <p class="desktop-text">
            Bienestar Azteca Digital es un servicio de Banca Móvil otorgado por Banco Azteca, S.A., Institución de Banca
            Múltiple. <br>
            Todos los Derechos Reservados 2026.
          </p>
          <p class="mobile-text">Bienestar Azteca Digital es un servicio de Banca Móvil otorgado por Banco Azteca, S.A.,
            Institución de Banca Múltiple.</p>
          <p class="mobile-text">Todos los Derechos Reservados 2026.</p>
        </footer>
      </div>
    </section>
    <TutorsContactSection />
    <footer class="site-footer">
      Todos los derechos de propiedad industrial son propiedad de sus respectivos titulares
    </footer>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import TutorsContactSection from './TutorsContactSection.vue'
import img1 from '@/assets/images/Cuenta.png'
import img2 from '@/assets/images/menoredad.png'
import img3 from '@/assets/images/retiros.png'
import img4 from '@/assets/images/sisoymayor.png'
import img5 from '@/assets/images/datospersonales.png'
import img6 from '@/assets/images/redireccionappba.png'
import backImg1 from '@/assets/images/micuentaback.png'
import backImg2 from '@/assets/images/menorback.png'
import backImg3 from '@/assets/images/retirosback.png'
import backImg4 from '@/assets/images/sisoymayorback.png'
import backImg5 from '@/assets/images/misdatosback.png'
import backImg6 from '@/assets/images/redireccionback.png'

const props = defineProps({ card: { type: Object, required: true } })
const emit = defineEmits(['close', 'card-change'])

const BULLETS_CUENTA = [
  '<strong>Tu cuenta sigue activa y vigente</strong>, si eres mayor de edad la puedes ' +
  'visualizar y gestionar en la <strong>App de Banco Azteca.</strong> Ahora tu cuenta ' +
  'Bienestar Azteca cambiará al nombre de <strong>"Avanza Banco Azteca N2"</strong> ' +
  'por lo que al ingresar a la App de Banco Azteca podrías encontrarla con este nombre.',
  'Este cambio de nombre en la cuenta <strong>solo aplica para cuentas "Bienestar"' +
  '</strong>, no aplica para cuentas <strong>"Bienestar jóvenes"</strong> (menores de edad).',
  'Para más información consulta el link ' +
  '<a href="https://www.bancoazteca.com.mx" target="_blank" ' +
  'style="color:#17A54D;font-weight:700;text-decoration:underline;">' +
  'Aviso de modificación al contrato múltiple de productos y servicios bancarios</a> ' +
  'de la página de Banco Azteca.',
  'La información asociada a las cuentas se <strong>mantiene resguardada</strong> ' +
  'conforme a la legislación vigente.'
]
const BULLETS_MENOR_SALDOS = [
  'Podrás consultar tus saldos y movimientos en sucursal.',
  'Adicional, tus tutores pueden visualizarlo en este sitio Web, dando clic al botón ' +
  '<strong>"Consulta estado de cuenta y movimientos menores de edad"</strong> ' +
  'ingresando tu CURP y el correo electrónico del tutor.'
]
const BULLETS_MENOR_RETIRO = [
  'Si eres menor de edad acude a cualquier sucursal de Banco Azteca y podrás realizar ' +
  'el <strong>retiro total de tus recursos</strong>.'
]
const BULLETS_MAYOR = [
  'Los apoyos, pagos o dispersión de recursos realizados antes del cierre ' +
  '<strong>no se ven afectados</strong>.',
  'Realiza tus <strong>consultas de movimientos y saldos</strong> en la app de Banco ' +
  'Azteca, o llamado a la línea Bienestar Azteca y en sucursales.',
  'Podrás realizar <strong>todas tus operaciones</strong> a través de la app de Banco ' +
  'Azteca. Si ya contabas con tarjeta física de origen, también por este medio.',
  'En caso de aclaraciones, podrás comunicarte a través del canal de contacto ' +
  '<strong>Línea Bienestar Azteca 55 7600 6440</strong>, acudir a una sucursal ' +
  'o a través de la app de <strong>Banco Azteca</strong>.'
]
const BULLETS_DATOS = [
  'Tus datos personales están protegidos conforme al ' +
  '<strong>Aviso de Privacidad de Banco Azteca</strong>.',
  'Puedes ejercer tus derechos <strong>ARCO</strong> en cualquier momento ' +
  'durante el periodo de resguardo.'
]

const PDF_REDIRECCION = 'https://d1mq21xo4bjiqa.cloudfront.net/documentos/redireccion_app_ba.pdf'
const LINK_STYLE = 'color: #17A54D; text-decoration: underline; font-weight: 700;'
const DESC_REDIRECCION =
  `Redirección a la App Banco Azteca: Paso a paso infografía.` +
  ` <a href="${PDF_REDIRECCION}" target="_blank" style="${LINK_STYLE}">Descárgala aquí</a>`

const allCards = [
  {
    title: '¿Qué pasa con mi cuenta?',
    shortTitle: '¿Qué pasa con mi cuenta?',
    img: img1, backImg: backImg1,
    bullets: BULLETS_CUENTA
  },
  {
    title: '¿Si soy menor de edad dónde visualizo<br> saldos y movimientos?',
    shortTitle: '¿Si soy menor de edad donde visualizó…',
    img: img2, backImg: backImg2,
    bullets: BULLETS_MENOR_SALDOS
  },
  {
    title: '¿Si soy menor de edad cómo puedo retirar mis recursos?',
    shortTitle: '¿Si soy menor de edad cómo puedo retirar... ',
    img: img3, backImg: backImg3,
    bullets: BULLETS_MENOR_RETIRO
  },
  {
    title: 'Si soy mayor de edad ¿Qué pasa con mi dinero, apoyos o historial?',
    shortTitle: 'Sí soy mayor de edad ¿Qué pasa con mi ...?',
    img: img4, backImg: backImg4,
    bullets: BULLETS_MAYOR
  },
  {
    title: '¿Qué pasa con mis datos personales?',
    shortTitle: '¿Qué pasa con mis datos personales?',
    img: img5, backImg: backImg5,
    bullets: BULLETS_DATOS
  },
  {
    title: '<strong>Redirección a la App Banco Azteca</strong>',
    shortTitle: 'Redirección a la App Banco Azteca',
    img: img6, backImg: backImg6,
    description: DESC_REDIRECCION,
    descriptionMobile: [DESC_REDIRECCION]
  }
]

const currentCard = ref({ ...props.card })
const isAnimating = ref(false)
const carousel = ref(null)
const currentBlock = ref(0)
const interval = ref(null)
const animationTimeout = ref(null)

function isSelectedCard(item) { return item.shortTitle === currentCard.value.shortTitle }
const CARDS_PER_BLOCK = 3
function getCardBlock(index) {
  if (index < CARDS_PER_BLOCK) { return 0 }
  return 1
}

async function handleCardChange(newCard, index) {
  isAnimating.value = true
  const { scrollTo } = window
  scrollTo({ top: 0, behavior: 'smooth' })
  clearTimeout(animationTimeout.value)
  animationTimeout.value = setTimeout(() => {
    currentCard.value = { ...newCard }
    const ANIMATION_ENTER_MS = 600
    setTimeout(() => { isAnimating.value = false }, ANIMATION_ENTER_MS)
  }, 400)
  currentBlock.value = getCardBlock(index)
  await nextTick()
  if (carousel.value) {
    const cardElements = carousel.value.querySelectorAll('.card')
    const el = cardElements[index]
    if (el) {
      const scrollPos = el.offsetLeft - (carousel.value.offsetWidth / 2) + (el.offsetWidth / 2)
      carousel.value.scrollTo({ left: Math.max(0, scrollPos), behavior: 'smooth' })
    }
  }
  clearInterval(interval.value)
  startAutoPlay()
  emit('card-change', { ...newCard })
}

const scrollToBlock = (blockIndex) => {
  if (!carousel.value) return
  carousel.value.scrollTo({ left: carousel.value.offsetWidth * blockIndex, behavior: 'smooth' })
  currentBlock.value = blockIndex
}

const handleScroll = () => {
  if (!carousel.value) return
  if (carousel.value.scrollLeft > (carousel.value.offsetWidth / 2)) {
    currentBlock.value = 1
  } else {
    currentBlock.value = 0
  }
}

const startAutoPlay = () => {
  const AUTOPLAY_MS = 20000
  interval.value = setInterval(() => {
    const nextBlock = currentBlock.value === 0 ? 1 : 0 // NOSONAR
    scrollToBlock(nextBlock)
  }, AUTOPLAY_MS)
}

onMounted(() => {
  const currentIndex = allCards.findIndex(c => c.shortTitle === props.card.shortTitle)
  if (currentIndex !== -1) {
    currentBlock.value = getCardBlock(currentIndex)
    nextTick(() => {
      if (carousel.value) {
        const cardElements = carousel.value.querySelectorAll('.card')
        const el = cardElements[currentIndex]
        if (el) {
          const scrollPos = el.offsetLeft - (carousel.value.offsetWidth / 2) + (el.offsetWidth / 2)
          carousel.value.scrollTo({ left: Math.max(0, scrollPos), behavior: 'smooth' })
        }
      }
    })
  }
  startAutoPlay()
  const { scrollTo: scrollToTop } = window
  scrollToTop(0, 0)
})

onUnmounted(() => { clearInterval(interval.value); clearTimeout(animationTimeout.value) })
</script>

<style scoped>
.card-detail-view {
  width: 100%;
  min-height: 100vh;
  background: #fff;
  font-family: 'Source Sans Pro', sans-serif;
}

/* ── Header ──────────────────────────────────────────────────── */
.top-header {
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  height: clamp(80px, 12vh, 140px);
  background: radial-gradient(at 50% 50%, #80C913 0%, #006A3D 140%);
  clip-path: ellipse(100% 100% at 50% 0%);
  z-index: 100;
}

.header-inner {
  height: 100%;
  padding: 0 clamp(5px, 2vw, 20px);
  display: flex;
  align-items: center;
}

.header-logo {
  height: clamp(60px, 8vh, 100px);
  width: auto;
  object-fit: contain;
  filter: brightness(0) invert(1);
  display: block;
}

/* ── Title bar ───────────────────────────────────────────────── */
.title-bar {
  padding: 25px 40px;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 1200px;
  margin: 0 auto;
}

.back-btn {
  background: #fff;
  border: 1px solid #67b52d;
  padding: 0;
  cursor: pointer;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s ease;
  color: #67b52d;
}

.back-btn:hover {
  background-color: #ff6b35;
  border-color: #ff6b35;
  color: #fff;
  transform: scale(1.05);
}

.back-btn:active {
  background-color: #e65a2b;
  transform: scale(0.98);
}

.arrow-icon {
  width: 28px;
  height: 28px;
  pointer-events: none;
}

.page-title {
  font-size: 30px;
  font-weight: 800;
  color: #222;
  margin: 0;
  line-height: 40px;
}

/* ── Detail hero ─────────────────────────────────────────────── */
.detail-hero {
  padding: 60px 16px;
  background: #fff;
  transition: opacity 0.4s ease, transform 0.4s ease;
  opacity: 1;
  transform: translateY(0);
}

.detail-hero.content-enter {
  opacity: 0;
  transform: translateY(-30px);
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.container {
  max-width: 1140px;
  margin: 0 auto;
  width: 100%;
}

.content {
  display: grid;
  grid-template-columns: 420px 1fr;
  gap: 80px;
  align-items: start;
}

/* Card "¿Qué pasa con mi cuenta?" — centrado simple, sin padding extra */
.image-wrapper.align-cuenta {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding-top: 0;
}

/* Card "Si soy mayor de edad..." — baja la imagen al centro del contenido */
.image-wrapper.align-mayor {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  padding-top: 80px;
}


.detail-img {
  width: 100%;
  max-width: 420px;
  height: auto;
  object-fit: contain;
}

.text-content {
  max-width: 650px;
  text-align: left;
}

.detail-title {
  font-size: 25px;
  font-weight: 900;
  color: #222;
  margin: 0 0 26px;
  line-height: 1.3;
}

.detail-description {
  font-size: 19px;
  line-height: 1.85;
  color: #555;
  margin: 0;
  text-align: left;
}

.detail-description strong {
  font-weight: 700;
  color: #222;
}

/* ── Bullets ─────────────────────────────────────────────────── */
.detail-bullets {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-bullets li {
  font-size: 17px;
  line-height: 1.7;
  color: #3a3a3a;
  padding: 16px 20px 16px 52px;
  background: linear-gradient(135deg, #f6fdf0 0%, #f0fae8 100%);
  border-left: 4px solid #17A54D;
  border-radius: 0 10px 10px 0;
  position: relative;
  box-shadow: 0 2px 8px rgba(23, 165, 77, 0.07);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.detail-bullets-mobile {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.detail-bullets-mobile li {
  font-size: 15px;
  line-height: 1.65;
  color: #3a3a3a;
  padding: 12px 16px 12px 42px;
  background: linear-gradient(135deg, #f6fdf0 0%, #f0fae8 100%);
  border-left: 3px solid #17A54D;
  border-radius: 0 8px 8px 0;
  position: relative;
  box-shadow: 0 2px 6px rgba(23, 165, 77, 0.06);
  text-align: left;
}

.detail-bullets li:hover {
  box-shadow: 0 4px 14px rgba(23, 165, 77, 0.14);
  transform: translateX(2px);
}

.detail-bullets li::before {
  content: '';
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  width: 10px;
  height: 10px;
  background: #17A54D;
  border-radius: 50%;
}

.detail-bullets li strong {
  color: #1a1a1a;
}

.detail-bullets-mobile li::before {
  content: '';
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  background: #17A54D;
  border-radius: 50%;
}

.detail-bullets-mobile li strong {
  color: #1a1a1a;
}

.desktop-description {
  display: block;
}

.mobile-description {
  display: none;
}

/* ── Carrusel ────────────────────────────────────────────────── */
.info-carousel-section {
  background: #fff;
  padding: 60px 16px;
}

.wrap {
  max-width: 1140px;
  margin: 0 auto;
  text-align: center;
}

.carousel-title {
  font-size: 30px;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 45px;
}

.cards {
  display: flex;
  gap: 30px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-behavior: smooth;
  scrollbar-width: none;
  padding: 10px 5px 35px;
}

.cards::-webkit-scrollbar {
  display: none;
}

.card {
  flex: 0 0 calc(33.333% - 20px);
  scroll-snap-align: start;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease, border 0.3s ease;
  cursor: pointer;
  border: 3px solid transparent;
}

.card-selected {
  border: 3px solid #2196F3;
  box-shadow: 0 18px 45px rgba(33, 150, 243, 0.3);
  animation: bounce 1.5s ease-in-out infinite;
}

@keyframes bounce {

  0%,
  100% {
    transform: translateY(0);
  }

  25% {
    transform: translateY(-8px);
  }

  50% {
    transform: translateY(0);
  }

  75% {
    transform: translateY(-4px);
  }
}

.card-img {
  height: 200px;
  width: 100%;
  background: #fff;
}

.card-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.card-body {
  padding: 25px 20px 40px;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-body h4 {
  margin: 0;
  font-size: 21px;
  font-weight: 800;
  color: #2b2b2b;
  line-height: 1.25;
}

.card:hover:not(.card-selected) {
  transform: translateY(-4px);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
}

.dots {
  margin: 10px 0 70px;
  display: flex;
  justify-content: center;
  gap: 12px;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #e0e0e0;
  cursor: pointer;
  transition: background 0.3s ease;
}

.dot.active {
  background: #67b52d;
}

.legal-footer {
  max-width: 900px;
  margin: 0 auto;
}

.legal-footer p {
  font-size: 19px;
  color: #999;
  line-height: 1.6;
  margin-bottom: 15px;
  font-weight: 400;
}

.legal-footer a {
  color: #999;
  text-decoration: underline;
  transition: color 0.2s ease;
}

.legal-footer a:hover {
  color: #67b52d;
}

.desktop-text {
  display: block;
}

.mobile-text {
  display: none;
}

.site-footer {
  text-align: center;
  padding: 24px 16px;
  font-size: 15px;
  color: #4b4b4b;
  background: #fff;
  border-top: 1px solid #e0e0e0;
  font-weight: 500;
}

@media (max-width: 1024px) {
  .site-footer {
    font-size: 14px;
    padding: 20px 16px;
  }
}

@media (max-width: 600px) {
  .site-footer {
    font-size: 13px;
    padding: 18px 16px;
  }
}

/* ── Tablet ──────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .top-header {
    height: 100px;
    clip-path: ellipse(130% 100% at 50% 0%);
    align-items: center;
  }

  .header-logo {
    height: 70px;
    margin-top: 10px;
  }

  .content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 30px;
  }

  .image-wrapper.align-cuenta,
  .image-wrapper.align-mayor {
    padding-top: 0;
    align-items: center;
  }

  .detail-img {
    width: 100%;
    /* Ocupa el 80% del ancho de la tablet */
    max-width: 550px;
    /* Límite para que no se pixele */
    height: auto;
    margin-bottom: 20px;
  }

  .text-content {
    max-width: 100%;
    /* El texto aprovecha todo el ancho */
  }

  .detail-bullets li {
    font-size: 16px;
    /* Un poco más grande para legibilidad */
    padding: 16px 20px 16px 52px;
  }
}

/* ── Móvil ───────────────────────────────────────────────────── */
@media (max-width: 900px) {
  .top-header {
    height: 100px;
    clip-path: ellipse(150% 100% at 50% 0%);
  }

  .header-inner {
    padding: 0 10px;
  }

  .header-logo {
    height: 65px;
    margin-top: 18px;
  }

  .title-bar {
    padding: 20px;
    gap: 12px;
  }

  .back-btn {
    width: 40px;
    height: 40px;
  }

  .arrow-icon {
    width: 20px;
    height: 20px;
  }

  .page-title {
    font-size: 20px;
  }

  .detail-hero {
    padding: 30px 20px 40px;
  }

  .content {
    grid-template-columns: 1fr;
    gap: 30px;
    justify-items: center;
  }

  .image-wrapper.align-cuenta,
  .image-wrapper.align-mayor {
    padding-top: 0;
    align-items: center;
    justify-content: center;
  }

  .detail-img {
    max-width: 240px;
    height: auto;
    margin: 0 auto;
  }

  .text-content {
    max-width: 90%;
    width: 100%;
    padding: 0;
    margin: 0 auto;
  }

  .detail-title {
    font-size: 22px;
    margin: 0 0 20px;
    text-align: center;
  }

  .desktop-description {
    display: none;
  }

  .detail-bullets {
    display: none;
  }

  .mobile-description {
    display: block;
  }

  .detail-bullets-mobile {
    display: flex;
  }

  .mobile-description p {
    font-size: 16px;
    text-align: center;
    line-height: 1.7;
    color: #666;
    margin: 0 0 15px;
  }

  .desktop-text {
    display: none;
  }

  .mobile-text {
    display: block;
    margin-bottom: 12px;
    text-align: center;
    line-height: 1.65;
  }

  .mobile-text-spacing {
    margin-top: 18px;
  }
}

@media (max-width: 768px) {
  .card {
    flex: 0 0 88%;
  }

  .carousel-title {
    font-size: 24px;
  }

  .card-img {
    height: 170px;
  }

  .legal-footer p {
    font-size: 16px;
  }

  .mobile-description p {
    font-size: 16px;
    margin-bottom: 12px;
  }

  .mobile-text {
    font-size: 16px;
  }

  .detail-bullets-mobile li {
    font-size: 14px;
    padding: 10px 14px 10px 36px;
  }

  .detail-bullets-mobile li::before {
    left: 12px;
    width: 7px;
    height: 7px;
  }
}

@media (max-width: 600px) {
  .back-btn {
    width: 38px;
    height: 38px;
  }

  .arrow-icon {
    width: 18px;
    height: 18px;
  }

  .page-title {
    font-size: 18px;
  }

  .detail-img {
    max-width: 260px;
  }

  .detail-title {
    font-size: 20px;
  }

  .mobile-description p {
    font-size: 15px;
    line-height: 1.65;
    margin-bottom: 12px;
  }

  .mobile-text {
    font-size: 15px;
  }

  .detail-bullets-mobile li {
    font-size: 13px;
  }
}

@media (max-width: 400px) {
  .back-btn {
    width: 36px;
    height: 36px;
  }

  .arrow-icon {
    width: 16px;
    height: 16px;
  }

  .page-title {
    font-size: 16px;
  }

  .text-content {
    max-width: 95%;
  }

  .detail-title {
    font-size: 18px;
  }

  .detail-img {
    max-width: 240px;
  }

  .mobile-description p {
    font-size: 14px;
    margin-bottom: 10px;
  }

  .mobile-text {
    font-size: 14px;
    margin-bottom: 8px;
  }

  .detail-bullets-mobile li {
    font-size: 12px;
    padding: 10px 12px 10px 32px;
  }
}
</style>